import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { EASE_SNAP, DURATION, usePrefersReducedMotion } from '../ui/motion';
import { tabListKeyDown } from '../ui/tabs';

export interface TabDef {
  key: string;
  label: string;
  /** Optional icon element — renders above the label when present. */
  icon?: ReactNode;
}

interface TabBarProps {
  tabs: TabDef[];
  active: string;
  onChange: (key: string) => void;
  id?: string;
  /** The DOM id of the panel the ACTIVE tab controls — only supplied for the
   *  tab that is actually mounted, so aria-controls never points at an element
   *  the app has not rendered (panels mount per tab, App.tsx). */
  panelFor?: (key: string) => string | undefined;
  /** Rendered after the tabs in the same bar — the 5th "More" slot
   *  (card yOcdjnR6pXBz). Not a tab: it opens the full menu instead of
   *  changing `active`, so it stays outside the measured indicator and
   *  outside the tablist — a tablist may only contain tabs. */
  trailing?: ReactNode;
  /** Card (founder report 2026-10-30) — the trailing slot is a DESTINATION, not
   *  just a button: Explore opens every screen and sub-view, and while one of
   *  those is on screen nothing in the bar was underlined, so a person who
   *  arrived through the menu saw the bar disagree with the page. When true the
   *  indicator moves under the trailing slot (`.more-slot`) instead of a tab. */
  trailingActive?: boolean;
}

/** Renders an icon with consistent 20×20 sizing and currentColor stroke. */
function TabIcon({ children }: { children: ReactNode }) {
  return (
    <span className="tab-icon" aria-hidden="true">
      {children}
    </span>
  );
}

/**
 * Tab bar with ONE sliding indicator instead of N independently recolouring
 * buttons — the JitsLab `BottomNav` technique, ported.
 *
 * Indicator position comes from the tab's measured DOM rect, not percentage
 * math, so it stays correct when labels differ in width.
 */
export function TabBar({ tabs, active, onChange, id, panelFor, trailing, trailingActive }: TabBarProps) {
  const containerRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  /* The indicator underlines the LABEL text, not the cell (card 28dxL5zVzfDe)
     — so it is measured from the label span when one is mounted. */
  const labelRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const [rect, setRect] = useState({ left: 0, width: 0, top: 0, ready: false });
  const reduced = usePrefersReducedMotion();
  const duration = reduced ? 0 : DURATION.slow;
  /* Orientation tracks the layout, not a prop: a horizontal bar on phones and
     a vertical side menu from 641px up (card ESnACmbVhCYd). ui/tabs.ts walks
     both axes with the same arrow keys, so this only tells assistive tech which
     one the user is looking at. */
  const [vertical, setVertical] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 641px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 641px)');
    const onChange = () => setVertical(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const measure = () => {
    const container = containerRef.current;
    const idx = tabs.findIndex(t => t.key === active);
    /* The Explore slot owns the indicator while a view it opened is on screen
       (see trailingActive). It is not a tab, so it is measured off its own
       button and its label span rather than the tab refs. */
    const moreEl = trailingActive ? container?.querySelector<HTMLElement>('.more-slot') ?? null : null;
    const el = moreEl ?? (idx >= 0 ? tabRefs.current[idx] : null);
    const label = moreEl
      ? moreEl.querySelector<HTMLElement>('.pear-tab-label')
      : idx >= 0 ? labelRefs.current[idx] : null;
    if (!container || !el) {
      setRect(r => ({ ...r, ready: false }));
      return;
    }
    const target = label ?? el;
    const tRect = target.getBoundingClientRect();
    const cRect = container.getBoundingClientRect();
    /* Absolute `top`/`left` resolve against the container's PADDING box, so
       the 1px borders have to come off the measured offsets. The label now
       carries a vertical padding box (card rKfOjiinhRsC) — the underline
       hugs the TEXT, so its padding comes off the measured bottom edge. */
    const cs = getComputedStyle(container);
    const borderY = parseFloat(cs.borderTopWidth) || 0;
    const borderX = parseFloat(cs.borderLeftWidth) || 0;
    const padB = label ? (parseFloat(getComputedStyle(label).paddingBottom) || 0) : 0;
    setRect({
      left: tRect.left - cRect.left - borderX,
      width: tRect.width,
      top: tRect.bottom - padB - cRect.top - borderY + 3,
      ready: true,
    });
  };

  useLayoutEffect(measure, [active, tabs, vertical, trailingActive]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tabs, trailingActive]);

  /* The first measure can run before the stylesheet/webfont has settled, so
     the underline lands a few px off its label (card 28dxL5zVzfDe). Re-measure
     once fonts are ready and once after the late layout pass. The ref keeps
     the LATEST measure: the closure captured here belongs to the mount
     render, so calling it after a tab switch would pin the underline to the
     tab that was active when the component mounted. */
  const measureRef = useRef(measure);
  measureRef.current = measure;
  useEffect(() => {
    const t = window.setTimeout(() => measureRef.current(), 250);
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready.then(() => measureRef.current()).catch(() => {});
    }
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <nav
      id={id}
      ref={containerRef}
      className="pear-tabs"
      aria-label="Sections"
    >
      <span
        className="pear-tab-indicator"
        aria-hidden="true"
        style={{
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          top: `${rect.top}px`,
          opacity: rect.ready ? 1 : 0,
          transition: reduced
            ? 'none'
            : `left ${duration}ms ${EASE_SNAP}, width ${duration}ms ${EASE_SNAP}, top ${duration}ms ${EASE_SNAP}, opacity 160ms linear`,
        }}
      />
      {/* The bar used to put role="tab" straight on children of a <nav> — a
          tab with no tablist around it, no panel association and no keyboard.
          The tablist is a real element now, it holds ONLY the tabs, and the
          roving tabindex + arrows come from ui/tabs.ts (UX item 6, H8). */}
      <div
        className="pear-tablist"
        role="tablist"
        aria-label="Sections"
        aria-orientation={vertical ? 'vertical' : 'horizontal'}
      >
        {tabs.map(tab => (
          <button
            key={tab.key}
            ref={el => {
              tabRefs.current[tabs.indexOf(tab)] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${tab.key}`}
            data-tab={tab.key}
            aria-selected={tab.key === active}
            aria-controls={panelFor?.(tab.key)}
            /* Roving tabindex: the bar is ONE tab stop, arrows move within it. */
            tabIndex={tab.key === active ? 0 : -1}
            onKeyDown={e => tabListKeyDown(e, el => onChange(el.dataset.tab ?? ''))}
            className={`pear-btn pear-tab${tab.key === active ? ' is-active' : ''}`}
            onClick={() => onChange(tab.key)}
          >
            {tab.icon && <TabIcon>{tab.icon}</TabIcon>}
            <span
              className="pear-tab-label"
              ref={el => {
                labelRefs.current[tabs.indexOf(tab)] = el;
              }}
            >
              {tab.label}
            </span>
          </button>
        ))}
      </div>
      {trailing}
    </nav>
  );
}
